/**
 * Checks if content appears to be HTML (from the rich text editor)
 * or plain text (legacy data that needs conversion).
 * Quill wraps content in HTML tags like <p>, so we check for common HTML patterns.
 *
 * @param {string} content - The content to check
 * @returns {boolean} - True if content appears to be HTML, false if plain text
 */
export function isHtmlContent(content) {
  if (!content) return false;
  const htmlPairPattern =
    /<(p|strong|em|u|ol|ul|li|h[1-6]|blockquote|a|span|div)\b[^>]*>[\s\S]*<\/\1\s*>/i;
  const htmlBrPattern = /<br\s*\/?>/i;

  if (typeof DOMParser === 'undefined') {
    return htmlPairPattern.test(content) || htmlBrPattern.test(content);
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const selector =
      'p,br,strong,em,u,ol,ul,li,h1,h2,h3,h4,h5,h6,blockquote,a,span,div';
    const hasAllowedElement = Boolean(
      doc.body && doc.body.querySelector(selector)
    );
    if (!hasAllowedElement) return false;
    return htmlPairPattern.test(content) || htmlBrPattern.test(content);
  } catch {
    return htmlPairPattern.test(content) || htmlBrPattern.test(content);
  }
}

/**
 * Escapes HTML special characters in plain text to prevent
 * them from being interpreted as HTML.
 *
 * @param {string} text - Plain text to escape
 * @returns {string} - HTML-escaped text with newlines converted to <br />
 */
export function escapeHtml(text) {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .replace(/\n/g, '<br />');
}

/**
 * Converts plain text to HTML suitable for display.
 * If content is already HTML, returns it as-is.
 * If content is plain text, escapes special characters and converts newlines.
 *
 * @param {string} content - Content to prepare for HTML display
 * @returns {string} - HTML-safe content ready for rendering
 */
export function prepareForHtmlDisplay(content) {
  return isHtmlContent(content) ? content : escapeHtml(content);
}

/**
 * Converts plain text to HTML suitable for the Quill editor.
 * Wraps content in <p> tags for proper Quill formatting.
 * If content is already HTML, returns it as-is.
 *
 * @param {string} content - Content to prepare for Quill editor
 * @returns {string} - HTML content ready for Quill
 */
export function prepareForQuillEditor(content) {
  if (isHtmlContent(content)) return content;
  if (!content) return '';

  const escaped = escapeHtml(content);
  // Wrap in <p> tags for proper Quill formatting
  return `<p>${escaped}</p>`;
}
